import React, { useEffect, useState, useCallback } from "react";
import AsyncSelect from "react-select/async";
import { Modal } from "react-bootstrap";
import {
  History, Search, SlidersHorizontal, Eye,
  Code, CheckCircle2, XCircle,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import DataTable from "../Common/DataTable";
import { PageHeader, UserAvatar } from "../ui";
import { FilterModal, FilterGroup } from "../ui/FilterModal";
import "./coding-history.scss";

const PAGE_SIZE = 15;
const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "solved", label: "Solved" },
  { value: "partially_solved", label: "Partial" },
  { value: "attempted", label: "Attempted" },
];
const DIFFICULTY_OPTIONS = [
  { value: "", label: "All Difficulties" },
  { value: "Easy", label: "Easy" },
  { value: "Medium", label: "Medium" },
  { value: "Hard", label: "Hard" },
];
const SORT_OPTIONS = [
  { value: "newest", label: "Newest First" },
  { value: "oldest", label: "Oldest First" },
  { value: "score-high", label: "Score High → Low" },
  { value: "score-low", label: "Score Low → High" },
];

const AdminCodingHistory = () => {
  const [submissions, setSubmissions] = useState([]);
  const [filters, setFilters] = useState({
    status: "", difficulty: "", language: "", search: "",
    studentId: "", batchId: "", sort: "newest",
  });
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [batches, setBatches] = useState([]);
  const [viewSubmission, setViewSubmission] = useState(null);
  const [selectedAttemptIndex, setSelectedAttemptIndex] = useState(0);

  useEffect(() => { setSelectedAttemptIndex(0); }, [viewSubmission]);

  // Fetch batches for filter
  useEffect(() => {
    apiClient.get("/batches/list?pageSize=500")
      .then(res => setBatches(res.data.batches || []))
      .catch(() => {});
  }, []);

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: PAGE_SIZE };
      if (filters.status) params.status = filters.status;
      if (filters.difficulty) params.difficulty = filters.difficulty;
      if (filters.language) params.language = filters.language;
      if (filters.search) params.search = filters.search;
      if (filters.studentId) params.studentId = filters.studentId;
      if (filters.batchId) params.batchId = filters.batchId;
      if (filters.sort) params.sort = filters.sort;

      const { data } = await apiClient.get("/practice/admin/submissions", { params });
      setSubmissions(data.submissions || []);
      setTotalRecords(data.total || 0);
    } catch {
      setSubmissions([]);
      setTotalRecords(0);
    }
    setLoading(false);
  }, [filters, page]);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  const loadStudentOptions = async (inputValue) => {
    try {
      const res = await apiClient.get("/students/list", {
        params: { search: inputValue, limit: 20 },
      });
      return (res.data.students || []).map(s => ({
        label: `${s.name} (${s.email})`,
        value: s._id,
      }));
    } catch { return []; }
  };

  const handleSearchBlur = () => {
    if (searchInput !== filters.search) {
      setFilters(f => ({ ...f, search: searchInput }));
      setPage(1);
    }
  };

  const handleResetFilters = () => {
    setFilters(f => ({
      ...f, status: "", difficulty: "", language: "", batchId: "", sort: "newest",
    }));
    setPage(1);
  };

  const activeFilterCount =
    (filters.status ? 1 : 0) +
    (filters.difficulty ? 1 : 0) +
    (filters.language ? 1 : 0) +
    (filters.batchId ? 1 : 0);

  const getStatusClass = (status) => {
    if (status === "solved") return "ch-status--solved";
    if (status === "partially_solved") return "ch-status--partial";
    return "ch-status--attempted";
  };

  const getStatusLabel = (status) => {
    if (status === "solved") return "Solved";
    if (status === "partially_solved") return "Partial";
    return "Attempted";
  };

  const getDifficultyClass = (d) => {
    const dl = d?.toLowerCase();
    if (dl === "easy") return "ch-diff--easy";
    if (dl === "medium") return "ch-diff--medium";
    if (dl === "hard") return "ch-diff--hard";
    return "";
  };

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-IN", {
      timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    }) : "-";

  const columns = [
    {
      key: "index",
      label: "#",
      width: "45px",
      render: (_, index) => (page - 1) * PAGE_SIZE + index + 1,
    },
    {
      key: "student",
      label: "Student",
      width: "20%",
      render: (s) => (
        <div className="d-flex align-items-center gap-2">
          <UserAvatar name={s.student?.name} size="sm" />
          <div>
            <div className="fw-medium">{s.student?.name || "-"}</div>
            <div className="text-muted ch-text-sm">{s.batch?.name || "-"}</div>
          </div>
        </div>
      ),
    },
    {
      key: "question",
      label: "Question",
      width: "22%",
      render: (s) => (
        <div>
          <div className="fw-medium ch-truncate" title={s.question?.title}>
            {s.question?.title || "-"}
          </div>
          <span className={`ch-diff-badge ${getDifficultyClass(s.question?.difficulty)}`}>
            {s.question?.difficulty || "-"}
          </span>
        </div>
      ),
    },
    {
      key: "language",
      label: "Language",
      width: "10%",
      render: (s) => <span className="ch-lang-badge">{s.latestLanguage || "-"}</span>,
    },
    {
      key: "score",
      label: "Score",
      width: "12%",
      render: (s) => {
        const scoreVal = s.bestScore ?? (s.totalCount > 0 ? Math.round((s.bestPassedCount / s.totalCount) * 100) : 0);
        const allPassed = scoreVal === 100;
        return (
          <div className="ch-score-cell">
            <span className={`ch-score-100 ${allPassed ? "ch-score-100--perfect" : scoreVal >= 50 ? "ch-score-100--good" : "ch-score-100--low"}`}>
              {scoreVal}<span className="ch-score-100-max">/100</span>
            </span>
            <span className="ch-score-tc-sub">{s.bestPassedCount}/{s.totalCount} test cases</span>
          </div>
        );
      },
    },
    {
      key: "status",
      label: "Status",
      width: "10%",
      render: (s) => (
        <span className={`ch-status-badge ${getStatusClass(s.latestStatus)}`}>
          {getStatusLabel(s.latestStatus)}
        </span>
      ),
    },
    {
      key: "date",
      label: "Last Attempt",
      width: "12%",
      render: (s) => <span className="ch-text-sm">{formatDate(s.latestDate)}</span>,
    },
    {
      key: "attempts",
      label: "Attempts",
      width: "80px",
      render: (s) => (
        <span className={`ch-attempts-badge${s.totalAttempts > 1 ? " ch-attempts-badge--multi" : ""}`}>
          {s.totalAttempts}
        </span>
      ),
    },
    {
      key: "actions",
      label: "",
      width: "40px",
      render: (s) => (
        <button
          className="ch-view-btn"
          onClick={(e) => {
            e.stopPropagation();
            setViewSubmission(s);
          }}
          aria-label="View code"
        >
          <Eye size={16} />
        </button>
      ),
    },
  ];

  // Custom select styles for dark/light theme
  const selectStyles = {
    control: (base) => ({
      ...base,
      backgroundColor: "var(--input-bg)",
      borderColor: "var(--border-primary)",
      borderRadius: "8px",
      minHeight: "38px",
      "&:hover": { borderColor: "var(--text-tertiary)" },
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: "var(--card-bg)",
      border: "1px solid var(--border-primary)",
      borderRadius: "8px",
      boxShadow: "0 4px 12px var(--card-shadow)",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? "#3b82f6" : state.isFocused ? "var(--bg-hover)" : "transparent",
      color: state.isSelected ? "#fff" : "var(--text-primary)",
      "&:hover": { backgroundColor: state.isSelected ? "#3b82f6" : "var(--bg-hover)" },
    }),
    singleValue: (base) => ({ ...base, color: "var(--text-primary)" }),
    input: (base) => ({ ...base, color: "var(--text-primary)" }),
    placeholder: (base) => ({ ...base, color: "var(--text-tertiary)" }),
    indicatorSeparator: (base) => ({ ...base, backgroundColor: "var(--border-primary)" }),
    dropdownIndicator: (base) => ({ ...base, color: "var(--text-tertiary)", "&:hover": { color: "var(--text-primary)" } }),
    clearIndicator: (base) => ({ ...base, color: "var(--text-tertiary)", "&:hover": { color: "var(--text-primary)" } }),
  };

  const currentAttempt = viewSubmission?.attempts?.[selectedAttemptIndex] ?? null;

  return (
    <div className="ch-page">
      <PageHeader
        icon={History}
        title="Coding Submissions"
        description="View all student coding practice submissions, code, and test results"
      />

      {/* Toolbar */}
      <div className="ch-toolbar">
        {/* Student AsyncSelect */}
        <div className="ch-student-select">
          <AsyncSelect
            cacheOptions
            defaultOptions
            loadOptions={loadStudentOptions}
            value={selectedStudent}
            onChange={(selected) => {
              setSelectedStudent(selected);
              setFilters(f => ({ ...f, studentId: selected?.value || "" }));
              setPage(1);
            }}
            placeholder="Search student..."
            isClearable
            styles={selectStyles}
          />
        </div>

        {/* Search */}
        <div className="ch-search">
          <Search size={16} className="ch-search-icon" />
          <input
            type="text"
            className="ch-search-input"
            placeholder="Search question or student..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onBlur={handleSearchBlur}
            onKeyDown={(e) => e.key === "Enter" && handleSearchBlur()}
          />
        </div>

        <div className="ch-toolbar-actions">
          {/* Desktop filters */}
          <select
            className="ch-filter-select d-none d-md-block"
            value={filters.status}
            onChange={(e) => { setFilters(f => ({ ...f, status: e.target.value })); setPage(1); }}
          >
            {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select
            className="ch-filter-select d-none d-md-block"
            value={filters.difficulty}
            onChange={(e) => { setFilters(f => ({ ...f, difficulty: e.target.value })); setPage(1); }}
          >
            {DIFFICULTY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select
            className="ch-filter-select d-none d-lg-block"
            value={filters.batchId}
            onChange={(e) => { setFilters(f => ({ ...f, batchId: e.target.value })); setPage(1); }}
          >
            <option value="">All Batches</option>
            {batches.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
          </select>
          <select
            className="ch-filter-select d-none d-lg-block"
            value={filters.sort}
            onChange={(e) => { setFilters(f => ({ ...f, sort: e.target.value })); setPage(1); }}
          >
            {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>

          {/* Mobile filter button */}
          <button
            className={`ch-filter-btn d-md-none${activeFilterCount > 0 ? " ch-filter-btn--active" : ""}`}
            onClick={() => setShowFilterModal(true)}
            aria-label="Open filters"
          >
            <SlidersHorizontal size={16} />
            {activeFilterCount > 0 && <span className="ch-filter-badge">{activeFilterCount}</span>}
          </button>
        </div>
      </div>

      {/* Data Table */}
      <DataTable
        data={submissions}
        columns={columns}
        loading={loading}
        hoverable
        pagination={{
          page,
          pageSize: PAGE_SIZE,
          total: totalRecords,
          onPageChange: setPage,
        }}
        emptyMessage="No coding submissions found"
        onRowClick={(row) => setViewSubmission(row)}
      />

      {/* Submission Detail Modal */}
      <Modal
        show={!!viewSubmission}
        onHide={() => setViewSubmission(null)}
        size="lg"
        centered
        dialogClassName="ch-modal"
      >
        {viewSubmission && (
          <>
            <Modal.Header closeButton className="ch-modal-header">
              <Modal.Title className="ch-modal-title">
                <Code size={18} />
                <span>{viewSubmission.question?.title || "Submission"}</span>
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="ch-modal-body">
              {/* Group meta */}
              <div className="ch-modal-meta">
                <div className="ch-modal-meta-item">
                  <UserAvatar name={viewSubmission.student?.name} size="sm" />
                  <span>{viewSubmission.student?.name}</span>
                </div>
                <span className="ch-lang-badge">{viewSubmission.latestLanguage}</span>
                <span className={`ch-status-badge ${getStatusClass(viewSubmission.latestStatus)}`}>
                  {getStatusLabel(viewSubmission.latestStatus)}
                </span>
                <span className={`ch-diff-badge ${getDifficultyClass(viewSubmission.question?.difficulty)}`}>
                  {viewSubmission.question?.difficulty}
                </span>
                <span className={`ch-score ${getStatusClass(viewSubmission.latestStatus)}`}>
                  Best: {viewSubmission.bestPassedCount}/{viewSubmission.totalCount}
                </span>
                <span className={`ch-score-100 ${(viewSubmission.bestScore ?? 0) === 100 ? "ch-score-100--perfect" : (viewSubmission.bestScore ?? 0) >= 50 ? "ch-score-100--good" : "ch-score-100--low"}`}>
                  {viewSubmission.bestScore ?? (viewSubmission.totalCount > 0 ? Math.round((viewSubmission.bestPassedCount / viewSubmission.totalCount) * 100) : 0)}/100
                </span>
              </div>

              {/* Attempt selector dropdown (multiple attempts only) */}
              {viewSubmission.totalAttempts > 1 && (
                <div className="ch-attempt-select-wrap">
                  <label className="ch-attempt-select-label">Attempt</label>
                  <select
                    className="ch-attempt-select"
                    value={selectedAttemptIndex}
                    onChange={(e) => setSelectedAttemptIndex(Number(e.target.value))}
                  >
                    {viewSubmission.attempts.map((a, i) => (
                      <option key={a._id} value={i}>
                        #{viewSubmission.totalAttempts - i} — {getStatusLabel(a.status)} · {a.passedCount}/{a.totalCount} · {a.totalCount > 0 ? Math.round((a.passedCount / a.totalCount) * 100) : 0}/100 · {formatDate(a.createdAt)}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Code + results for selected attempt */}
              {currentAttempt && (
                <>
                  <div className="ch-code-section">
                    <div className="ch-code-header">
                      <Code size={14} />
                      <span>Submitted Code</span>
                      <span className="ch-lang-badge">{currentAttempt.language}</span>
                    </div>
                    <pre className="ch-code-block">{currentAttempt.code || "No code submitted"}</pre>
                  </div>

                  {currentAttempt.results?.length > 0 && (
                    <div className="ch-results-section">
                      <div className="ch-code-header">
                        <CheckCircle2 size={14} />
                        <span>Test Results ({currentAttempt.passedCount}/{currentAttempt.totalCount} passed)</span>
                      </div>
                      <div className="ch-results-grid">
                        {currentAttempt.results.map((r, i) => (
                          <div key={i} className={`ch-result-card ${r.passed ? "ch-result--pass" : "ch-result--fail"}`}>
                            <div className="ch-result-header">
                              {r.passed ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
                              <span>Test #{i + 1}</span>
                              <span className={`ch-result-tag ${r.passed ? "ch-result-tag--pass" : "ch-result-tag--fail"}`}>
                                {r.passed ? "Passed" : "Failed"}
                              </span>
                            </div>
                            <div className="ch-result-rows">
                              <div className="ch-result-row">
                                <span className="ch-result-label">Input</span>
                                <pre className="ch-result-pre">{r.input || "(empty)"}</pre>
                              </div>
                              <div className="ch-result-row">
                                <span className="ch-result-label">Expected</span>
                                <pre className="ch-result-pre">{r.expected}</pre>
                              </div>
                              <div className="ch-result-row">
                                <span className="ch-result-label">Output</span>
                                <pre className="ch-result-pre">{r.output || "(empty)"}</pre>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </Modal.Body>
          </>
        )}
      </Modal>

      {/* Mobile Filter Modal */}
      <FilterModal
        show={showFilterModal}
        onHide={() => setShowFilterModal(false)}
        onReset={handleResetFilters}
      >
        <FilterGroup label="Status">
          <select
            value={filters.status}
            onChange={(e) => { setFilters(f => ({ ...f, status: e.target.value })); setPage(1); }}
          >
            {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </FilterGroup>
        <FilterGroup label="Difficulty">
          <select
            value={filters.difficulty}
            onChange={(e) => { setFilters(f => ({ ...f, difficulty: e.target.value })); setPage(1); }}
          >
            {DIFFICULTY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </FilterGroup>
        <FilterGroup label="Batch">
          <select
            value={filters.batchId}
            onChange={(e) => { setFilters(f => ({ ...f, batchId: e.target.value })); setPage(1); }}
          >
            <option value="">All Batches</option>
            {batches.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
          </select>
        </FilterGroup>
        <FilterGroup label="Sort By">
          <select
            value={filters.sort}
            onChange={(e) => { setFilters(f => ({ ...f, sort: e.target.value })); setPage(1); }}
          >
            {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </FilterGroup>
      </FilterModal>
    </div>
  );
};

export default AdminCodingHistory;
